import React from "react";
import { Col, DatePicker, Form, Row, Typography } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";

import "./PushNotifications.style.scss";
import { Button } from "components/Buttons/Buttons";
import { FormInput, FormInputArea } from "components/FormItems/FlatFormItems";
import { sendBroadcastNotification } from "services/pushNotifications.service";

const { Text } = Typography;

const DATE_TIME_FORMAT = "MM/DD/YYYY hh:mm A";

const disabledDate = (current) => current && current.isBefore(moment().startOf("day"));

const disabledValidTillDate = (current, validFrom) => {
  if (!current) return false;
  if (disabledDate(current)) return true;
  if (validFrom && current.clone().endOf("day").diff(validFrom, "days", true) < 1) {
    return true;
  }
  return false;
};

const disabledTime = (current) => {
  const now = moment();
  const isToday = current && current.isSame(now, "day");
  return {
    disabledHours: () =>
      isToday ? Array.from({ length: now.hour() }, (_, i) => i) : [],
    disabledMinutes: () =>
      isToday && current.hour() === now.hour()
        ? Array.from({ length: now.minute() }, (_, i) => i)
        : [],
  };
};

export default function PushNotifications() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const validFrom = Form.useWatch("validFrom", form);

  const handleSend = (values) => {
    dispatch(
      sendBroadcastNotification({
        title: values.title,
        message: values.message,
        validFrom: values.validFrom ? values.validFrom.toISOString() : null,
        validTill: values.validTill ? values.validTill.toISOString() : null,
      })
    ).then((result) => {
      if (result?.status === "success") {
        form.resetFields();
      }
    });
  };

  return (
    <div className="pushNotificationsSettings">
      <Row>
        <Col xs={24} md={18} lg={14}>
          <Text
            style={{
              display: "block",
              fontSize: 14,
              color: "#3C475F",
              marginBottom: 24,
            }}
          >
            Send a custom push notification to every registered mobile
            device.
          </Text>

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={handleSend}
            onFinishFailed={({ errorFields }) => {
              form.scrollToField(errorFields[0].name, {
                scrollMode: "if-needed",
                block: "center",
                behavior: "smooth",
              });
            }}
          >
            <Row gutter={[24, 0]}>
              <Col xs={24}>
                <FormInput
                  name="title"
                  variant="underlined"
                  label="Title"
                  required
                  max={200}
                  placeholder="Notification Title"
                />
              </Col>
              <Col xs={24}>
                <FormInputArea
                  name="message"
                  variant="underlined"
                  label="Message"
                  required
                  rows={5}
                  placeholder="Notification Message"
                />
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="validFrom"
                  label="Valid From"
                  className="flatFormItem underlinedDatePicker"
                  initialValue={moment()}
                >
                  <DatePicker
                    showTime
                    format={DATE_TIME_FORMAT}
                    style={{ width: "100%" }}
                    placeholder="Immediately"
                    disabledDate={disabledDate}
                    disabledTime={disabledTime}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="validTill"
                  label="Valid Till"
                  className="flatFormItem underlinedDatePicker"
                  dependencies={["validFrom"]}
                  rules={[
                    {
                      required: true,
                      message: "Required",
                    },
                    {
                      validator: (_, value) => {
                        const validFrom = form.getFieldValue("validFrom");
                        if (!value || !validFrom) return Promise.resolve();
                        if (value.diff(validFrom, "days", true) >= 1)
                          return Promise.resolve();
                        return Promise.reject(
                          new Error(
                            "Valid Till must be at least 1 day after Valid From"
                          )
                        );
                      },
                    },
                  ]}
                >
                  <DatePicker
                    showTime
                    format={DATE_TIME_FORMAT}
                    style={{ width: "100%" }}
                    placeholder="Expiration Date & Time"
                    disabledDate={(current) =>
                      disabledValidTillDate(current, validFrom)
                    }
                    disabledTime={disabledTime}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end">
              <Button
                rounded
                type="primary"
                htmlType="submit"
                style={{ height: 50, minWidth: 220 }}
              >
                Send Notification
              </Button>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
}
